
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDistricts, fetchMunicipalities } from "../redux/actions/locationActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import { fetchParkDetailsByLocation, createParkUser } from "../redux/actions/ParkUserCreateActions";
import theme from "../components/shared/theme";
import toast from "react-hot-toast";
import Loader from "../components/shared/Loader"; // Adjust path as needed

const ParkCreateUser = () => {
  const dispatch = useDispatch();

  // ========== Redux state ==========
  const { districts = [], municipalities = [] } = useSelector((state) => state.location || {});
  const { designations = [] } = useSelector((state) => state.designation || {});

  const { parkDetails = [], parkDetailsLoading, parkDetailsError } = useSelector(
    (state) => state.parkEmployeeCreate || {}
  );

  const { createUserLoading, createUserError, createdUser } = useSelector(
    (state) => state.parkEmployeeCreate || {}
  );

  // ========== Local state ==========
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    district_id: "",
    municipality_id: "",
    project_type: "",
    designation: "",
    doj: "",
    park_id: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedDistrictName, setSelectedDistrictName] = useState("");
  const [selectedMunicipalityName, setSelectedMunicipalityName] = useState("");
  const [selectedPark, setSelectedPark] = useState(null);
  const [toastIds, setToastIds] = useState({});
  const [formStep, setFormStep] = useState(1);
  const [pageLoading, setPageLoading] = useState(true); // Initial page load

  // Fetch initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        await Promise.all([
          dispatch(fetchDistricts()),
          dispatch(fetchMunicipalities()),
          dispatch(fetchDesignations())
        ]);
      } catch (error) {
        toast.error("Failed to load initial data");
      } finally {
        setPageLoading(false);
      }
    };
    
    loadInitialData();
  }, [dispatch]);

  // Fetch parks when district, municipality, or project type changes
  useEffect(() => {
    if (formData.district_id && formData.municipality_id) {
      dispatch(
        fetchParkDetailsByLocation({
          district_id: formData.district_id,
          municipality_id: formData.municipality_id,
          project_type: formData.project_type || undefined,
        })
      );
    }
  }, [dispatch, formData.district_id, formData.municipality_id, formData.project_type]);

  // Reset dependent fields
  useEffect(() => {
    setFormData((prev) => ({ ...prev, municipality_id: "", park_id: "" }));
    setSelectedMunicipalityName("");
    setSelectedPark(null);
    setErrors((prev) => ({ ...prev, municipality_id: "", park_id: "" }));
  }, [formData.district_id]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, park_id: "" }));
    setSelectedPark(null);
    setErrors((prev) => ({ ...prev, park_id: "" }));
  }, [formData.municipality_id]);

  // Update district/municipality names
  useEffect(() => {
    const district = districts.find((d) => d.id === Number(formData.district_id));
    setSelectedDistrictName(district?.name || "");
  }, [formData.district_id, districts]);

  useEffect(() => {
    const municipality = municipalities.find((m) => m.id === Number(formData.municipality_id));
    setSelectedMunicipalityName(municipality?.name || "");
  }, [formData.municipality_id, municipalities]);

  // Auto-fill park details when a park is selected
  useEffect(() => {
    if (formData.park_id) {
      const park = parkDetails.find((p) => p.park_id === Number(formData.park_id));
      setSelectedPark(park || null);
    } else {
      setSelectedPark(null);
    }
  }, [formData.park_id, parkDetails]);

  // Handle success toast
  useEffect(() => {
    if (createdUser) {
      toast.success("Park user created successfully", { 
        id: "create-success",
        icon: "🎉",
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      setFormData({
        name: "",
        phone: "",
        district_id: "",
        municipality_id: "",
        project_type: "",
        designation: "",
        doj: "",
        park_id: "",
      });
      setSelectedPark(null);
      setErrors({});
      setTouched({});
      setFormStep(1);
    }
  }, [createdUser]);

  // Handle error toasts
  useEffect(() => {
    if (createUserError && !toastIds.createError) {
      toast.error(createUserError, { 
        id: "create-error",
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      setToastIds((prev) => ({ ...prev, createError: true }));
    }
  }, [createUserError, toastIds.createError]);

  useEffect(() => {
    if (parkDetailsError && !toastIds.parkError) {
      toast.error(parkDetailsError, { 
        id: "park-error",
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      setToastIds((prev) => ({ ...prev, parkError: true }));
    }
  }, [parkDetailsError, toastIds.parkError]);

  useEffect(() => {
    if (!createUserError) {
      setToastIds((prev) => ({ ...prev, createError: false }));
    }
  }, [createUserError]);

  useEffect(() => {
    if (!parkDetailsError) {
      setToastIds((prev) => ({ ...prev, parkError: false }));
    }
  }, [parkDetailsError]);

  // ========== Validation ==========
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value.trim()) error = "Name is required";
        else if (value.trim().length < 2) error = "Name must be at least 2 characters";
        break;
      case "phone":
        if (!value) error = "Phone is required";
        else if (!/^\d{10}$/.test(value)) error = "Phone must be exactly 10 digits";
        break;
      case "district_id":
        if (!value) error = "District is required";
        break;
      case "municipality_id":
        if (!value) error = "Municipality is required";
        break;
      case "designation":
        if (!value) error = "Designation is required";
        break;
      case "doj":
        if (!value) error = "Date of Joining is required";
        else {
          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate > today) error = "Date of Joining cannot be in the future";
        }
        break;
      case "park_id":
        if (!value) error = "Park is required";
        break;
      default:
        break;
    }
    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      if (key !== "project_type") {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const allTouched = {};
    Object.keys(formData).forEach(key => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!validateForm()) {
      toast.error("Please fix the errors in the form", { 
        id: "validation-error",
        icon: "⚠️",
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }

    if (!selectedPark) {
      toast.error("Selected park not found", { 
        id: "park-not-found",
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      phone: formData.phone,
      district_id: Number(formData.district_id),
      municipality_id: Number(formData.municipality_id),
      admin_id: selectedPark.admin_id,
      park_id: Number(formData.park_id),
      municipality: selectedMunicipalityName,
      ward: selectedPark.ward,
      designation: formData.designation,
      district: selectedDistrictName,
      doj: formData.doj,
    };

    dispatch(createParkUser(payload));
  };

  const getError = (field) => errors[field] && touched[field] && (
    <span style={errorTextStyle}>
      <span style={{ marginRight: '4px' }}>⚠️</span>
      {errors[field]}
    </span>
  );

  // Update form step based on completed fields
  useEffect(() => {
    if (formData.district_id && formData.municipality_id) {
      setFormStep(2);
      if (formData.park_id) {
        setFormStep(3);
        if (formData.designation && formData.name && formData.phone && formData.doj) {
          setFormStep(4);
        }
      }
    } else {
      setFormStep(1);
    }
  }, [formData]);

  // Show page loader while initial data is loading
  if (pageLoading) {
    return (
      <div style={pageLoaderContainerStyle}>
        <Loader size="large" text="Loading Park Create User..." variant="spinner" />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Form-level loader for park details */}
      {parkDetailsLoading && (
        <div style={formLoaderOverlayStyle}>
          <Loader size="medium" text="Loading parks..." variant="dots" />
        </div>
      )}

      <div style={cardStyle}>
        {/* Header with icon */}
        <div style={headerContainerStyle}>
          <div style={iconContainerStyle}>
            <span style={iconStyle}>🌳</span>
          </div>
          <div>
            <h1 style={headerStyle}>Create Park User</h1>
            <p style={subheaderStyle}>Add a new team member to manage park operations</p>
          </div>
        </div>

        {/* Progress Steps */}
        <div style={progressContainerStyle}>
          <div style={progressTrackStyle}>
            <div 
              style={{ 
                ...progressFillStyle, 
                width: `${(formStep / 4) * 100}%` 
              }} 
            />
          </div>
          <div style={stepsContainerStyle}>
            <div style={stepItemStyle}>
              <div style={{ ...stepDotStyle, background: formStep >= 1 ? '#10b981' : '#e2e8f0' }} />
              <span style={{ ...stepLabelStyle, color: formStep >= 1 ? '#10b981' : '#94a3b8' }}>Location</span>
            </div>
            <div style={stepItemStyle}>
              <div style={{ ...stepDotStyle, background: formStep >= 2 ? '#10b981' : '#e2e8f0' }} />
              <span style={{ ...stepLabelStyle, color: formStep >= 2 ? '#10b981' : '#94a3b8' }}>Park</span>
            </div>
            <div style={stepItemStyle}>
              <div style={{ ...stepDotStyle, background: formStep >= 3 ? '#10b981' : '#e2e8f0' }} />
              <span style={{ ...stepLabelStyle, color: formStep >= 3 ? '#10b981' : '#94a3b8' }}>Details</span>
            </div>
            <div style={stepItemStyle}>
              <div style={{ ...stepDotStyle, background: formStep >= 4 ? '#10b981' : '#e2e8f0' }} />
              <span style={{ ...stepLabelStyle, color: formStep >= 4 ? '#10b981' : '#94a3b8' }}>Ready</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div style={gridStyle}>
            {/* District - Left Column */}
            <div style={fieldContainerStyle}>
              <label style={labelStyle}>
                District <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <select
                  name="district_id"
                  value={formData.district_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={parkDetailsLoading}
                  style={{
                    ...inputStyle,
                    borderColor: errors.district_id && touched.district_id ? "#ef4444" : "#e2e8f0",
                    opacity: parkDetailsLoading ? 0.7 : 1,
                  }}
                >
                  <option value="">Select District</option>
                  {districts.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
                {formData.district_id && !errors.district_id && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("district_id")}
            </div>

            {/* Municipality - Right Column */}
            <div style={fieldContainerStyle}>
              <label style={labelStyle}>
                Municipality <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <select
                  name="municipality_id"
                  value={formData.municipality_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={!formData.district_id || parkDetailsLoading}
                  style={{
                    ...inputStyle,
                    borderColor: errors.municipality_id && touched.municipality_id ? "#ef4444" : "#e2e8f0",
                    backgroundColor: !formData.district_id ? "#f8fafc" : "white",
                    cursor: !formData.district_id ? "not-allowed" : "pointer",
                    opacity: parkDetailsLoading ? 0.7 : 1,
                  }}
                >
                  <option value="">Select Municipality</option>
                  {municipalities
                    .filter((m) => m.district_id === Number(formData.district_id))
                    .map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                </select>
                {formData.municipality_id && !errors.municipality_id && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("municipality_id")}
            </div>

            {/* Project Type - Full Width */}
            <div style={{ ...fieldContainerStyle, gridColumn: "span 2" }}>
              <label style={labelStyle}>
                Project Type <span style={optionalStyle}>(optional)</span>
              </label>
              <select
                name="project_type"
                value={formData.project_type}
                onChange={handleChange}
                disabled={parkDetailsLoading}
                style={{
                  ...inputStyle,
                  opacity: parkDetailsLoading ? 0.7 : 1,
                }}
              >
                <option value="">All Types</option>
                <option value="Park">Park</option>
                <option value="Stretch">Stretch</option>
                <option value="Shelter">Shelter</option>
              </select>
            </div>

            {/* Park - Full Width */}
            <div style={{ ...fieldContainerStyle, gridColumn: "span 2" }}>
              <label style={labelStyle}>
                Park <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <select
                  name="park_id"
                  value={formData.park_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={!formData.municipality_id || parkDetailsLoading}
                  style={{
                    ...inputStyle,
                    borderColor: errors.park_id && touched.park_id ? "#ef4444" : "#e2e8f0",
                    backgroundColor: !formData.municipality_id || parkDetailsLoading ? "#f8fafc" : "white",
                  }}
                >
                  <option value="">
                    {parkDetailsLoading
                      ? "Loading parks..."
                      : !formData.municipality_id
                      ? "Select municipality first"
                      : parkDetails.length === 0
                      ? "No parks found"
                      : "Select Park"}
                  </option>
                  {parkDetails.map((park) => (
                    <option key={park.park_id} value={park.park_id}>
                      {park.project_code} – {park.park_stretch_name} (Ward {park.ward})
                    </option>
                  ))}
                </select>
                {formData.park_id && !errors.park_id && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("park_id")}
            </div>

            {/* Selected Park Summary */}
            {selectedPark && (
              <div style={summaryStyle}>
                <div style={summaryHeaderStyle}>
                  <span style={summaryIconStyle}>✨</span>
                  <span style={summaryHeaderTextStyle}>Selected Park Details</span>
                </div>
                <div style={summaryContentStyle}>
                  <div style={summaryItemStyle}>
                    <span style={summaryLabelStyle}>Project Code</span>
                    <span style={summaryValueStyle}>{selectedPark.project_code}</span>
                  </div>
                  <div style={summaryItemStyle}>
                    <span style={summaryLabelStyle}>Ward</span>
                    <span style={summaryValueStyle}>{selectedPark.ward}</span>
                  </div>
                  <div style={summaryItemStyle}>
                    <span style={summaryLabelStyle}>Park Name</span>
                    <span style={summaryValueStyle}>{selectedPark.park_stretch_name}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Designation - Full Width */}
            <div style={{ ...fieldContainerStyle, gridColumn: "span 2" }}>
              <label style={labelStyle}>
                Designation <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <select
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={parkDetailsLoading}
                  style={{
                    ...inputStyle,
                    borderColor: errors.designation && touched.designation ? "#ef4444" : "#e2e8f0",
                    opacity: parkDetailsLoading ? 0.7 : 1,
                  }}
                >
                  <option value="">Select Designation</option>
                  {designations.map((d, i) => (
                    <option key={i} value={d}>{d}</option>
                  ))}
                </select>
                {formData.designation && !errors.designation && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("designation")}
            </div>

            {/* Name - Left Column */}
            <div style={fieldContainerStyle}>
              <label style={labelStyle}>
                Name <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={parkDetailsLoading}
                  placeholder="Enter full name"
                  style={{
                    ...inputStyle,
                    borderColor: errors.name && touched.name ? "#ef4444" : "#e2e8f0",
                    opacity: parkDetailsLoading ? 0.7 : 1,
                  }}
                />
                {formData.name && !errors.name && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("name")}
            </div>

            {/* Phone - Right Column */}
            <div style={fieldContainerStyle}>
              <label style={labelStyle}>
                Phone <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={parkDetailsLoading}
                  placeholder="10 digit mobile number"
                  style={{
                    ...inputStyle,
                    borderColor: errors.phone && touched.phone ? "#ef4444" : "#e2e8f0",
                    opacity: parkDetailsLoading ? 0.7 : 1,
                  }}
                />
                {formData.phone && !errors.phone && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("phone")}
            </div>

            {/* Date of Joining - Full Width */}
            <div style={{ ...fieldContainerStyle, gridColumn: "span 2" }}>
              <label style={labelStyle}>
                Date of Joining <span style={requiredStarStyle}>*</span>
              </label>
              <div style={inputWrapperStyle}>
                <input
                  type="date"
                  name="doj"
                  value={formData.doj}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={parkDetailsLoading}
                  style={{
                    ...inputStyle,
                    borderColor: errors.doj && touched.doj ? "#ef4444" : "#e2e8f0",
                    opacity: parkDetailsLoading ? 0.7 : 1,
                  }}
                />
                {formData.doj && !errors.doj && !parkDetailsLoading && (
                  <span style={checkmarkStyle}>✓</span>
                )}
              </div>
              {getError("doj")}
            </div>
          </div>

          {/* Submit Button with Loader */}
          <div style={buttonContainerStyle}>
            <button
              type="submit"
              disabled={createUserLoading || parkDetailsLoading}
              style={createUserLoading || parkDetailsLoading ? disabledButtonStyle : buttonStyle}
              onMouseEnter={(e) => {
                if (!createUserLoading && !parkDetailsLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(16, 185, 129, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!createUserLoading && !parkDetailsLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(16, 185, 129, 0.2)';
                }
              }}
            >
              {createUserLoading ? (
                <span style={buttonContentStyle}>
                  <Loader size="small" color="white" variant="spinner" />
                  <span>Creating...</span>
                </span>
              ) : (
                <span style={buttonContentStyle}>
                  Create User
                  <span style={buttonIconStyle}>→</span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ========== Styles ==========
const containerStyle = {
  padding: theme.spacing.lg,
  backgroundColor: '#ffffff',
  minHeight: '100vh',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  position: 'relative',
};

const pageLoaderContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
};

const formLoaderOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const cardStyle = {
  maxWidth: '900px',
  margin: '0 auto',
  background: '#ffffff',
  borderRadius: '24px',
  boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.1), 0 10px 20px -10px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f0f0f0',
  padding: '40px',
  animation: 'slideUp 0.5s ease',
  position: 'relative',
};

const headerContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '32px',
};

const iconContainerStyle = {
  width: '60px',
  height: '60px',
  borderRadius: '18px',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 10px 20px -5px rgba(16, 185, 129, 0.3)',
};

const iconStyle = {
  fontSize: '32px',
};

const headerStyle = {
  fontSize: '32px',
  fontWeight: 700,
  color: '#111827',
  margin: '0',
  letterSpacing: '-0.02em',
};

const subheaderStyle = {
  fontSize: '16px',
  color: '#6b7280',
  margin: '4px 0 0 0',
};

const progressContainerStyle = {
  marginBottom: '32px',
};

const progressTrackStyle = {
  height: '6px',
  background: '#f0f0f0',
  borderRadius: '3px',
  overflow: 'hidden',
  marginBottom: '12px',
};

const progressFillStyle = {
  height: '100%',
  background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
  borderRadius: '3px',
  transition: 'width 0.3s ease',
};

const stepsContainerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 4px',
};

const stepItemStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const stepDotStyle = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  transition: 'background 0.3s ease',
};

const stepLabelStyle = {
  fontSize: '12px',
  fontWeight: 500,
  transition: 'color 0.3s ease',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
};

const fieldContainerStyle = {
  position: 'relative',
};

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: '8px',
};

const requiredStarStyle = {
  color: '#ef4444',
};

const optionalStyle = {
  color: '#9ca3af',
  fontSize: '12px',
  fontWeight: 400,
};

const inputWrapperStyle = {
  position: 'relative',
};

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: '12px',
  border: '2px solid #e5e7eb',
  fontSize: '15px',
  backgroundColor: '#ffffff',
  transition: 'all 0.2s ease',
  outline: 'none',
  boxSizing: 'border-box',
  color: '#111827',
};

const checkmarkStyle = {
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#10b981',
  fontWeight: 'bold',
  fontSize: '18px',
  pointerEvents: 'none',
};

const errorTextStyle = {
  color: '#ef4444',
  fontSize: '12px',
  marginTop: '6px',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const summaryStyle = {
  gridColumn: 'span 2',
  background: '#f9fafb',
  padding: '20px',
  borderRadius: '16px',
  border: '1px solid #e5e7eb',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)',
  animation: 'fadeIn 0.5s ease',
};

const summaryHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '16px',
};

const summaryIconStyle = {
  fontSize: '20px',
};

const summaryHeaderTextStyle = {
  fontSize: '16px',
  fontWeight: 600,
  color: '#059669',
};

const summaryContentStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
};

const summaryItemStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const summaryLabelStyle = {
  fontSize: '12px',
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const summaryValueStyle = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#111827',
};

const buttonContainerStyle = {
  marginTop: '40px',
  textAlign: 'right',
};

const buttonStyle = {
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  color: 'white',
  padding: '16px 40px',
  borderRadius: '40px',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: '16px',
  minWidth: '200px',
  boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)',
  transition: 'all 0.2s ease',
  position: 'relative',
  overflow: 'hidden',
};

const disabledButtonStyle = {
  ...buttonStyle,
  background: '#d1d5db',
  cursor: 'not-allowed',
  boxShadow: 'none',
  opacity: 0.8,
};

const buttonContentStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
};

const buttonIconStyle = {
  fontSize: '20px',
  transition: 'transform 0.2s ease',
};

export default ParkCreateUser;